import { QueryClient, UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { EventData, EventNames, useTracking } from "lib/tracking";
import { graphql } from "../../../../generated/app-service-gql";
import {
  BidQuery,
  BidsQuery,
  UpdateBidsMutation,
  UpdateBidsMutationVariables,
} from "../../../../generated/app-service-gql/graphql";
import { useGraphQlMutation } from "../../useGraphQlClient";
import { updateBidQuery, updateSearchBidsQuery } from "./update_cache/updateBidEntityCache";
import { BidDetails } from "./useBid";
import { BidWorkflow, useBidWorkflow } from "./useBidWorkflow";

const UPDATE_BIDS_MUTATION = graphql(`
  mutation updateBids($updateBidsRequest: UpdateBidsRequest!) {
    updateBids(UpdateBidsRequest: $updateBidsRequest) {
      id
      name
      description
      value
      createdAt
      updatedAt
      clarificationDeadline
      submissionDate
      startDate
      assignedTo {
        guid
        firstName
        lastName
        active
      }
      stage {
        id
        name
        color
        iconColor
        iconName
        transitionGroup
      }
      lead {
        id
        name
      }
    }
  }
`);

export type UpdateBidsRequestData = UpdateBidsMutationVariables["updateBidsRequest"]["updateData"];
type UpdateBidsData = UpdateBidsMutation["updateBids"][number];

export function useUpdateBids(
  options?: UseMutationOptions<UpdateBidsMutation, unknown, UpdateBidsMutationVariables, unknown>,
) {
  const queryClient = useQueryClient();

  const { logEvent } = useTracking();

  const { data: workflow } = useBidWorkflow();

  const { data, ...rest } = useGraphQlMutation(UPDATE_BIDS_MUTATION, {
    ...options,
    onSuccess: async (data, variables, context) => {
      // Update bids list caches (plural query)
      updateUseBidsCache(queryClient, data.updateBids);

      // Track field updates for analytics
      const fieldsThatHaveBeenUpdated = Object.fromEntries(
        Object.entries(variables.updateBidsRequest.updateData).filter(([, v]) => v !== undefined),
      );

      let previousBids: BidDetails[] = [];

      const hasUpdatedStage = Object.keys(fieldsThatHaveBeenUpdated).includes("stageId");

      // Only fetch previous bid data if stage has been updated
      if (hasUpdatedStage) {
        previousBids = getAllPreviousBids(queryClient, variables.updateBidsRequest.ids);
      }

      for (const updatedBid of data.updateBids) {
        for (const [key, newVal] of Object.entries(fieldsThatHaveBeenUpdated)) {
          if (key === "assignedToId" && newVal === null) {
            logEvent(EventNames.bidDetailsUpdated, {
              "Bid GUID": updatedBid.id,
              "Field updated": fieldToLabel[key] || key,
              "Updated field content": "Unassigned",
              "Bid title": updatedBid.name,
            });
          } else if (newVal !== undefined) {
            logEvent(EventNames.bidDetailsUpdated, {
              "Bid GUID": updatedBid.id,
              "Field updated": fieldToLabel[key] || key,
              "Updated field content": newVal,
              "Bid title": updatedBid.name,
            });
          }

          // We want to track if there's been a forward progression or lost stage update
          if (key === "stageId" && !!newVal) {
            const previousBid = previousBids.find((l) => l.id === updatedBid.id);

            // Shouldn't happen, but failsafe
            if (!previousBid) continue;

            trackBidStageChange(logEvent, previousBid, workflow, updatedBid);
          }
        }
      }

      // Update individual bid caches (singular query)
      // - crucial that this is at the end because we may check previously cached bid data above
      updateUseBidCache(queryClient, data.updateBids);

      options?.onSuccess?.(data, variables, context);
    },
  });

  return { data, ...rest };
}

/**
 * Tracks bid stage changes to amplitude, including whether the bid was lost or progressed,
 * as well as the previous and new stage names and order, bid ID, value, and title
 * @param logEvent
 * @param previousBid
 * @param workflow
 * @param updatedBid
 */
function trackBidStageChange(
  logEvent: (eventName: EventNames, eventData?: EventData) => void,
  previousBid: BidDetails,
  workflow: BidWorkflow | undefined,
  updatedBid: UpdateBidsData,
) {
  const stageTransitions = workflow?.transitions ?? [];

  // Need to use this method as workflow data from useBidWorkflow has the order field
  const startBidTransitionStage = stageTransitions.find(
    (transition) => transition.stage.id === previousBid.stage.id,
  );

  const endBidTransitionStage = stageTransitions.find(
    (transition) => transition.stage.id === updatedBid.stage.id,
  );
  const eventData: EventData = {
    "Start bid stage name": previousBid.stage.name,
    "End bid stage name": updatedBid.stage.name,
    "Start bid stage order": startBidTransitionStage?.order,
    "End bid stage order": endBidTransitionStage?.order,
    "Bid ID": updatedBid.id,
    "Bid value": updatedBid.value,
    "Bid stage group name": updatedBid.stage.transitionGroup,
    "Bid title": updatedBid.name,
  };

  const isForwardProgression =
    (endBidTransitionStage?.order ?? 0) > (startBidTransitionStage?.order ?? 0);

  if (endBidTransitionStage?.group === "Lost") {
    logEvent(EventNames.bidLost, eventData);
  } else if (isForwardProgression) {
    logEvent(EventNames.bidProgressed, eventData);
  }
}

/**
 * Updates any bids in the useBid cache (singular query) assigned to any of the updated bids
 */
function updateUseBidCache(
  queryClient: QueryClient,
  responseData: UpdateBidsMutation["updateBids"],
) {
  const updatedBidIds = responseData.map((b) => b.id);

  const matchingBidQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: ["bid"] })
    .filter((q) => {
      const [, queryBidId] = q.queryKey as [string, string];
      return updatedBidIds.includes(queryBidId);
    });

  for (const bidQuery of matchingBidQueries) {
    const [, bidId] = bidQuery.queryKey as [string, string];

    updateBidQuery(queryClient, bidId, (prevData) => {
      const newBidData = responseData.find((b) => b.id === prevData.id);
      return {
        ...prevData,
        ...newBidData,
      };
    });
  }
}

/**
 * Updates any bids in the useBids cache (plural query) assigned to any of the updated bids.
 * Computes value delta per filter group (queryKey[1]) so that all pages within the
 * same filter get a consistent valueSum update, without affecting unrelated filter caches.
 */
export function updateUseBidsCache(
  queryClient: QueryClient,
  responseData: UpdateBidsMutation["updateBids"],
) {
  const allBidsQueries = queryClient.getQueryCache().findAll({ queryKey: ["bids"] });
  const updatedBidIds = responseData.map((b) => b.id);

  // Compute value delta per filter group (queryKey[1] = filter/request params).
  // Each distinct filter has its own valueSum, so we must not apply a delta from
  // one filter group to another.
  const deltaByFilterKey = new Map<string, number>();
  const processedKeys = new Set<string>(); // `${filterKey}:${bidId}` — avoid double-counting

  for (const { queryKey } of allBidsQueries) {
    const data = queryClient.getQueryData<BidsQuery>(queryKey);
    if (!data) continue;
    const filterKey = JSON.stringify(queryKey[1]);
    for (const updatedBid of responseData) {
      if (processedKeys.has(`${filterKey}:${updatedBid.id}`)) continue;
      const cached = data.bids.results.find((b) => b.id === updatedBid.id);
      if (cached !== undefined) {
        processedKeys.add(`${filterKey}:${updatedBid.id}`);
        deltaByFilterKey.set(
          filterKey,
          (deltaByFilterKey.get(filterKey) ?? 0) + (updatedBid.value ?? 0) - (cached.value ?? 0),
        );
      }
    }
  }

  for (const { queryKey } of allBidsQueries) {
    const filterKey = JSON.stringify(queryKey[1]);
    updateSearchBidsQuery(
      queryClient,
      queryKey,
      updatedBidIds,
      (prevData) => {
        const newBidData = responseData.find((b) => b.id === prevData.id);
        return { ...prevData, ...newBidData };
      },
      deltaByFilterKey.get(filterKey), // undefined for unrelated filter groups → no override
    );
  }
}

/**
 * Gets all previous bid data from the useBid cache for the given list of updated bid IDs
 * @param queryClient
 * @param updatedBidIds
 * @returns
 */
function getAllPreviousBids(queryClient: QueryClient, updatedBidIds: string[]): BidDetails[] {
  const matchingBidQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: ["bid"] })
    .filter((q) => {
      const [, queryBidId] = q.queryKey as [string, string];
      return updatedBidIds.includes(queryBidId);
    });

  const previousBids: BidQuery["bid"][] = [];

  for (const q of matchingBidQueries) {
    const data = queryClient.getQueryData<BidQuery>(q.queryKey);
    if (data?.bid) {
      previousBids.push(data.bid);
    }
  }

  return previousBids;
}

const fieldToLabel: Record<string, string> = {
  name: "Title",
  description: "Description",
  value: "Value",
  stageId: "Stage",
  assignedToId: "Owner",
  clarificationDeadline: "Clarification deadline",
  submissionDate: "Submission date",
  startDate: "Start date",
  leadId: "Related lead",
};
